import React, { useContext } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useCurrency } from '@/hooks/useCurrency';
import { type ClientSalesReport } from '@/types';
import { Users, Receipt, Calendar } from 'lucide-react';
import dayjs from 'dayjs';

interface ClientSalesReportTableProps {
  data: ClientSalesReport[];
  isLoading: boolean;
  error?: Error | null;
}

export const ClientSalesReportTable: React.FC<ClientSalesReportTableProps> = ({
  data,
  isLoading,
  error,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const { formatAmount } = useCurrency();

  if (isLoading) {
    return (
      <div className="w-full space-y-3 py-4">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-10 w-full" />
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-12 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-destructive">
        <p className="text-lg font-semibold">{translator('Global.error')}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {error.message || translator('Errors.unexpectedError')}
        </p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-3">
          <Users className="h-6 w-6" />
        </div>
        <p className="text-lg font-medium text-foreground">
          {translator('Clients.noSavedClientSalesFound')}
        </p>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          {translator('Clients.noSavedClientSalesDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">{translator('Clients.client')}</TableHead>
            <TableHead className="font-semibold">{translator('Clients.identification')}</TableHead>
            <TableHead className="font-semibold text-right">{translator('Clients.totalPurchases')}</TableHead>
            <TableHead className="font-semibold text-right">{translator('Clients.transactionCount')}</TableHead>
            <TableHead className="font-semibold text-right">{translator('Clients.lastPurchaseDate')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={`${row.clientName}-${index}`} className="hover:bg-muted/30 transition-colors">
              <TableCell className="font-medium text-foreground">
                <div className="flex items-center gap-2">
                  <span className="truncate">{row.clientName}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {row.identification || '—'}
              </TableCell>
              <TableCell className="text-right font-semibold text-foreground">
                {formatAmount(row.totalSales)}
              </TableCell>
              <TableCell className="text-right text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  <Receipt className="h-3 w-3" />
                  {row.transactionCount}
                </span>
              </TableCell>
              <TableCell className="text-right text-muted-foreground text-sm">
                <span className="inline-flex items-center gap-1">
                  {dayjs(row.lastPurchaseDate).format('DD-MM-YYYY')}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
